import { assessmentParse } from "@p0security/shared/assessment/dsl";
import {
  DiscoverMatch,
  discoverPaths,
} from "@p0security/shared/graph/discover";
import { isParseWarning } from "@p0security/shared/graph/dsl/index";
import { dslParse as dslParser } from "@p0security/shared/graph/dsl/index";
import {
  DEFAULT_GRAPH_SEARCH_SETTINGS,
  GraphSearchSettings,
} from "@p0security/shared/graph/settings";
import {
  ConnectedNode,
  DirectedGraph,
  NodeOf,
} from "@p0security/shared/graph/types";
import {
  AssessmentNodes,
  isTargetNodeType,
} from "@p0security/shared/types/assessment/data/index";
import { Drawer, Spin, Typography } from "antd";
import { SelectedEnvironmentContext } from "components/Environment/contexts/SelectedEnvironmentContext";
import { DiscoverVisualization } from "components/GraphTable/DiscoverVisualization";
import { Heading } from "components/Heading";
import { useGuardedEffect } from "hooks/useGuardedEffect";
import { useLocalStorage } from "hooks/useLocalStorage";
import {
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router";
import { staticError } from "utils/console";

import { NodeDisplay } from "../components/node/NodeDisplay";
import { AggregatedGraphContext } from "../contexts/AggregatedGraphContext";
import { DiscoverVisualizationProvider } from "../contexts/DiscoverVisualizationContext";
import { ScopeContext } from "../contexts/ScopeContext";
import { useControls } from "../hooks/useControls";
import { useNavigateWithEnv } from "../hooks/useNavigateWithEnv";
import { nodeDataFromShow } from "./node";
import { NodeProperties } from "./node/NodeProperties";
import { NodeText } from "./node/NodeText";
import { AssessmentCollapse, AssessmentNodeRender } from "./node/renderer";

const ResultDetailFitViewOptions = {
  maxZoom: 0.8,
};

export const ResultDetailContainer: React.FC<
  React.PropsWithChildren<object>
> = ({ children }) => (
  // 108px subtraction here is necessary to correct for padding / headings
  <div style={{ minHeight: "calc(100% - 108px)", position: "relative" }}>
    {children}
  </div>
);

export const ResultDetailDrawer: React.FC<
  React.PropsWithChildren<{ title: ReactNode }>
> = ({ children, title }) => {
  const location = useLocation();
  const navigate = useNavigateWithEnv();

  const closeDrawer = useCallback(
    () => navigate({ ...location, pathname: "../.." }, { relative: "path" }),
    [location, navigate]
  );

  return (
    <Drawer
      push={false}
      styles={{ body: { padding: "12px" } }}
      getContainer={false}
      mask={false}
      onClose={closeDrawer}
      open
      title={<div style={{ display: "flex", gap: "0.3em" }}>{title}</div>}
      width={"max(50%, min(80%, 1000px))"}
    >
      {children}
    </Drawer>
  );
};

export type ResultDetailProps = {
  header?: ReactNode;
  mode: "finding" | "query result";
  node: { key: string; type: string };
  terms: string;
  title?: ReactNode;
};

export const ResultDetail = ({
  header,
  mode,
  node: { key, type },
  terms,
  title,
}: ResultDetailProps): ReactElement => {
  const { last } = useContext(SelectedEnvironmentContext);
  const { loading } = last;
  const { provider, step } = useContext(ScopeContext);
  const { aggregated } = useContext(AggregatedGraphContext);
  const { controls } = useControls();
  const [results, setResults] =
    useState<DiscoverMatch<DirectedGraph<AssessmentNodes>>[]>();
  const [settings, _] = useLocalStorage<GraphSearchSettings>(
    "graph-search-settings"
  );

  const node = useMemo(
    () => aggregated?.nodes.find((n) => n.type === type && n.key === key),
    [aggregated?.nodes, key, type]
  );

  // Show query path if there is any non-empty query
  const showPaths = useMemo(() => !!terms?.trim(), [terms]);

  const stopOn = useMemo(
    () =>
      mode === "finding"
        ? []
        : (settings ?? DEFAULT_GRAPH_SEARCH_SETTINGS).stopOn,
    [mode, settings]
  );

  useGuardedEffect(
    (cancellation) => async () => {
      setResults(undefined);
      if (!node) return;
      // Only ever one node
      const graph = {
        nodes: [node as ConnectedNode<AssessmentNodes, keyof AssessmentNodes>],
      };
      const search = assessmentParse(terms)(dslParser);
      if (isParseWarning(search)) return;
      const [discovered] = await discoverPaths({
        graph,
        from: nodeDataFromShow[controls.show].predicate,
        settings: { stopOn },
        search,
      });
      const results = discovered?.matches;
      if (!results) return cancellation.guard(setResults)(undefined);
      setResults([{ node: node, matches: results }]);
    },
    [node, controls.show, terms, stopOn],
    staticError
  );

  const nodeTitle = useMemo(
    () =>
      title ??
      (node &&
        NodeText(node, provider, { noHover: true, hideThis: true })[
          node.type
        ]?.(node as any)),
    [node, provider, title]
  );

  const nodeProperties = useCallback(
    (node: NodeOf<AssessmentNodes>) => (
      <NodeProperties node={node} provider={provider} />
    ),
    [provider]
  );

  return (
    <ResultDetailDrawer title={nodeTitle}>
      {header}
      {loading || !node || step !== "aggregating" ? (
        <Spin />
      ) : !node || !isTargetNodeType(node) ? (
        <Heading title="Not Found" />
      ) : (
        <div style={{ width: "100%" }} data-testid={"result-detail"}>
          {showPaths && !!results && (
            <>
              <Typography.Title level={4} style={{ marginTop: "0.5em" }}>
                {mode === "query result" ? "Query Path" : "Attack Path"}
              </Typography.Title>
              <DiscoverVisualizationProvider provider={provider}>
                <DiscoverVisualization<AssessmentNodes>
                  collapse={AssessmentCollapse}
                  fitView={true}
                  fitViewOptions={ResultDetailFitViewOptions}
                  highlight={mode === "query result"}
                  matches={results}
                  properties={nodeProperties}
                  render={AssessmentNodeRender}
                  settings={{
                    maxResults: 1,
                    maxPaths: 100,
                    stopOn,
                  }}
                  viewStyle={{ height: "500px", width: "100%" }}
                />
              </DiscoverVisualizationProvider>
            </>
          )}
          <Typography.Title level={4} style={{ marginTop: "0.5em" }}>
            Details
          </Typography.Title>
          <div style={{ maxWidth: "1000px" }}>
            <NodeDisplay node={node} />
          </div>
        </div>
      )}
    </ResultDetailDrawer>
  );
};
